<template lang="pug">
el-dialog(
    v-focus-trap="dialogVisible"
    :title="$t('Media library')"
    :visible.sync="dialogVisible"
    :close-on-click-modal="false"
    :append-to-body="true"
    custom-class="image-library-dialog"
    width="1200px"
    top="16px"
    @open="handleOpen"
    @closed="handleClosed"
)
    .image-library
        el-tabs.image-library__tabs(v-model="activeTab")
            el-tab-pane(:label="$t('Upload files')" name="upload")
                .image-library__upload-tab
                    .image-library__upload-panel
                        h4 {{ $t('Upload files') }}
                        p {{ uploadDescription }}
                        Upload(
                            :accept="uploadAccept"
                            :tip-filetypes="uploadTipFiletypes"
                            @change="handleUploadChange"
                        )

            el-tab-pane(:label="$t('Media library')" name="library")
                .image-library__library-tab
                    .image-library__toolbar
                        el-input.image-library__search(
                            v-model.trim="filters.query"
                            clearable
                            prefix-icon="el-icon-search"
                            :placeholder="searchPlaceholder"
                            @input="handleSearchInput"
                            @clear="handleSearchClear"
                        )
                        .image-library__toolbar-actions
                            el-button(
                                plain
                                icon="el-icon-fa-filter fas"
                                :type="filtersVisible || hasActiveFilters ? 'primary' : 'default'"
                                @click="toggleFilters"
                            ) {{ $t('Filters') }}
                    .image-library__selection-bar(v-if="multiple")
                        p.image-library__selection-hint {{ multipleSelectionHint }}
                        .image-library__selection-chip
                            span.image-library__selection-status(v-if="hasSelection") {{ selectedCountLabel }}
                            button.image-library__selection-action(
                                v-if="hasSelection"
                                type="button"
                                @click="clearSelection"
                            ) {{ $t('Clear selection') }}
                    .image-library__filters(v-if="filtersVisible")
                        .image-library__filters-title {{ $t('Filter media') }}
                        .image-library__filters-controls
                            el-select.image-library__filter-select(
                                v-model="filters.types"
                                multiple
                                clearable
                                :placeholder="$t('Filter by type')"
                                @change="handleFilterChange"
                            )
                                el-option(
                                    v-for="option in mediaTypeOptions"
                                    :key="option.value"
                                    :label="$t(option.label)"
                                    :value="option.value"
                                )
                                    .image-library__select-option
                                        i(:class="option.icon")
                                        span {{ $t(option.label) }}
                            el-date-picker.image-library__filter-month(
                                v-model="filters.createdAtMonth"
                                type="month"
                                clearable
                                format="MMMM yyyy"
                                value-format="yyyy-MM"
                                :editable="false"
                                :placeholder="$t('Filter by month')"
                                @change="handleFilterChange"
                            )
                            el-select.image-library__filter-select(
                                v-if="tagFilterOptions.length"
                                v-model="filters.tags"
                                multiple
                                filterable
                                collapse-tags
                                clearable
                                :placeholder="$t('Filter by tags')"
                                @change="handleFilterChange"
                            )
                                el-option(
                                    v-for="tag in tagFilterOptions"
                                    :key="tag.key"
                                    :label="tag.name"
                                    :value="tag.key"
                                )
                            el-button(
                                plain
                                :disabled="!hasActiveFilters"
                                @click="clearFilters"
                            ) {{ $t('Reset filters') }}

                    .image-library__body(:class="{ 'image-library__body--with-sidebar': !!selectedFile }")
                        .image-library__browser(
                            ref="browser"
                            v-loading="loading"
                            @scroll.passive="handleBrowserScroll"
                        )
                            .image-library__grid(v-if="files.length")
                                el-card.image-library__item(
                                    v-for="file in files"
                                    :key="file.id"
                                    shadow="hover"
                                    :body-style="{ padding: '0px' }"
                                    :class="{ 'is-active': isSelected(file), 'is-focused': isFocused(file) }"
                                    @click.native="handleFileClick(file)"
                                )
                                    .image-library__item-content
                                        .image-library__thumb(
                                            :class="{ 'image-library__thumb--loading': canPreviewImage(file) && !isGridThumbLoaded(file) }"
                                        )
                                            //- Visibility dot — the same global .indicator marker the /files list uses.
                                            el-tooltip(
                                                effect="dark"
                                                :content="$t(file.visibility === 'public' ? 'Public' : 'Private')"
                                                placement="top"
                                            )
                                                span.image-library__visibility.indicator(:class="file.visibility === 'public' ? 'success' : 'danger'")
                                            .image-library__checkbox(v-if="multiple")
                                                el-checkbox(
                                                    :value="isSelected(file)"
                                                    :disabled="!isSelected(file) && hasReachedSelectionLimit"
                                                    @change="toggleFileSelection(file)"
                                                    @click.native.stop="stopEvent"
                                                )
                                            .image-library__thumb-skeleton(
                                                v-if="canPreviewImage(file) && !isGridThumbLoaded(file)"
                                                aria-hidden="true"
                                            )
                                            img.image-library__thumb-image(
                                                v-if="canPreviewImage(file)"
                                                :key="getGridThumbLoadKey(file)"
                                                loading="lazy"
                                                :src="getPreviewUrl(file, 260, 180)"
                                                :alt="file.preferred_name || file.name"
                                                @load="handleGridThumbLoad(file)"
                                                @error="handleGridThumbLoad(file)"
                                            )
                                            .image-library__file-thumb(v-else)
                                                i.fas(:class="getFileIconClass(file)")
                                                span {{ getFileExtension(file) }}
                                            //- Non-destructive variant stack: a badge that
                                            //- opens a popover to PICK a specific crop / AI
                                            //- edit instead of the root image — keeping the
                                            //- generated files out of the grid.
                                            el-popover(
                                                v-if="file.imageVariantCount > 0"
                                                placement="right-start"
                                                trigger="click"
                                                :width="248"
                                                popper-class="variant-stack-popover"
                                                :value="variantPopoverFileId === file.id"
                                                @input="open => variantPopoverFileId = open ? file.id : null"
                                            )
                                                .variant-stack
                                                    .variant-stack__head
                                                        i.el-icon-fa-layer-group.fas
                                                        span {{ $t('Versions') }} ({{ (file.imageVariants || []).length }})
                                                    .variant-stack__list
                                                        .variant-stack__item.is-selectable(
                                                            v-for="variant in (file.imageVariants || [])"
                                                            :key="variant.id"
                                                            @click="pickVariant(file, variant)"
                                                        )
                                                            .variant-stack__thumb
                                                                img(
                                                                    v-if="variant.file && variant.file.url"
                                                                    :src="variant.file.url"
                                                                    :alt="variant.label || variant.kind"
                                                                    loading="lazy"
                                                                )
                                                            .variant-stack__info
                                                                .variant-stack__kind
                                                                    i.fas(:class="variantKindIcon(variant.kind)")
                                                                    span {{ variantKindLabel(variant.kind) }}
                                                                .variant-stack__label(v-if="variant.label") {{ variant.label }}
                                                                .variant-stack__dims(v-if="variant.width && variant.height") {{ variant.width }} × {{ variant.height }} px
                                                .image-library__variant-badge(slot="reference" @click.stop)
                                                    i.el-icon-fa-layer-group.fas
                                                    span {{ file.imageVariantCount }}
                                        .image-library__meta
                                            .image-library__name {{ file.preferred_name || file.name }}
                                            .image-library__details {{ formatFileMeta(file) }}
                                            .image-library__tags(v-if="getFileTags(file).length")
                                                el-tag(
                                                    v-for="tag in getFileTags(file).slice(0, 3)"
                                                    :key="`${file.id}:${tag.id || tag.key || tag.name}`"
                                                    type="info"
                                                    effect="plain"
                                                    size="mini"
                                                ) {{ tag.name }}
                                                span.image-library__tags-more(v-if="getFileTags(file).length > 3") +{{ getFileTags(file).length - 3 }}

                            .image-library__empty(v-else-if="!loading")
                                i.el-icon-picture-outline
                                p {{ emptyStateLabel }}

                            .image-library__loading-more(v-if="loadingMore")
                                i.el-icon-loading
                                span {{ $t('Loading') }}...

                        .image-library__sidebar(v-if="selectedFile")
                            .image-library__selection
                                h4 {{ $t('Attachment details') }}
                                .image-library__selected-preview(:class="{ 'image-library__selected-preview--file': !hasMediaPreview(selectedFile) }")
                                    .image-library__selected-image-stage(
                                        v-if="canPreviewImage(selectedFile)"
                                    )
                                        .image-library__selected-image-skeleton(v-if="!selectedImagePreviewUrl" aria-hidden="true")
                                        img.image-library__selected-image(
                                            v-if="selectedImagePreviewUrl"
                                            :key="`detail-image:${selectedFile.id}:${selectedImageLoadKey}`"
                                            :src="selectedImagePreviewUrl"
                                            :alt="selectedFile.preferred_name || selectedFile.name"
                                            @load="handleSelectedImageLoad(selectedImageLoadKey)"
                                            @error="handleSelectedImageError(selectedImageLoadKey)"
                                        )
                                    video.image-library__selected-video(
                                        v-else-if="canPreviewVideo(selectedFile)"
                                        :key="`video-preview-${selectedFile.id}`"
                                        controls
                                        playsinline
                                        preload="metadata"
                                    )
                                        source(:src="getFileAccessUrl(selectedFile)" :type="selectedFile.mime")
                                    .image-library__selected-audio(v-else-if="canPreviewAudio(selectedFile)")
                                        i.fas(:class="getFileIconClass(selectedFile)")
                                        audio(
                                            :key="`audio-preview-${selectedFile.id}`"
                                            controls
                                            preload="metadata"
                                        )
                                            source(:src="getFileAccessUrl(selectedFile)" :type="selectedFile.mime")
                                    .image-library__selected-file(v-else)
                                        i.fas(:class="getFileIconClass(selectedFile)")
                                        span {{ getFileExtension(selectedFile) }}
                                .image-library__detail
                                    strong {{ $t('Name') }}
                                    span {{ selectedFile.preferred_name || selectedFile.name }}
                                .image-library__detail
                                    strong {{ $t('Type') }}
                                    span {{ selectedFile.mime }}
                                .image-library__detail
                                    strong {{ $t('Size') }}
                                    span {{ formatFileSize(selectedFile.size) }}
                                .image-library__detail
                                    strong {{ $t('Date') }}
                                    span {{ formatFileDate(selectedFile.created_at) || '-' }}
                                .image-library__detail(v-if="canPreviewImage(selectedFile)")
                                    strong {{ $t('Dimensions') }}
                                    span(v-if="selectedDimensions") {{ selectedDimensions.width }} x {{ selectedDimensions.height }} px
                                    span(v-else-if="selectedDimensionsLoading") {{ $t('Loading') }}...
                                    span(v-else) -
                                .image-library__detail(v-if="selectedFileTags.length")
                                    strong {{ $t('Tags') }}
                                    .image-library__detail-tags
                                        el-tag(
                                            v-for="tag in selectedFileTags"
                                            :key="`detail:${selectedFile.id}:${tag.id || tag.key || tag.name}`"
                                            type="info"
                                            effect="plain"
                                            size="small"
                                        ) {{ tag.name }}
                                //- Art. 50 KI-VO status, read-only: this dialog PICKS a file, it
                                //- does not manage one. Editing provenance here would change a
                                //- shared file as a side effect of choosing it, and the dialog
                                //- has no Save to confirm against. Managed in the file manager.
                                .image-library__detail(v-if="selectedFile.ai_disclosure === true")
                                    strong {{ $t('AI labelling') }}
                                    span {{ aiDisclosureLabel(selectedFile) }}
                                //- Non-destructive versions (crops / AI edits) of the selected
                                //- image. Clicking a row picks that variant's baked pixels.
                                .image-library__detail.image-library__detail--variants(v-if="supportsVersions && (selectedFile.imageVariants || []).length")
                                    strong {{ $t('Versions') }}
                                    .image-library__variant-list
                                        .image-library__variant(
                                            v-for="variant in (selectedFile.imageVariants || [])"
                                            :key="variant.id"
                                            role="button"
                                            tabindex="0"
                                            @click="pickVariant(selectedFile, variant)"
                                            @keyup.enter="pickVariant(selectedFile, variant)"
                                        )
                                            .image-library__variant-thumb
                                                img(
                                                    v-if="variant.file && variant.file.url"
                                                    :src="variant.file.url"
                                                    :alt="variant.label || variant.kind"
                                                    loading="lazy"
                                                )
                                                i.fas(v-else :class="variantKindIcon(variant.kind)")
                                            .image-library__variant-body
                                                .image-library__variant-kind
                                                    i.fas(:class="variantKindIcon(variant.kind)")
                                                    span {{ variantKindLabel(variant.kind) }}
                                                .image-library__variant-label(v-if="variant.label") {{ variant.label }}
                                                .image-library__variant-dims(v-if="variant.width && variant.height") {{ variant.width }} × {{ variant.height }} px
                                            i.image-library__variant-pick.fas.el-icon-fa-arrow-right

    template(slot="footer")
        .modal-footer
            .left
                el-button(
                    size="mini"
                    icon="el-icon-fa-times fas"
                    @click="dialogVisible = false"
                ) {{ $t('Cancel') }}
            .right
                el-button(
                    v-if="showCropButton && isImageSelection && !multiple"
                    size="mini"
                    icon="el-icon-fa-crop-alt fas"
                    :disabled="activeTab !== 'library' || !hasSelection"
                    :loading="selecting"
                    @click="confirmSelectionWithCrop"
                ) {{ $t('Set crop') }}
                el-button(
                    size="mini"
                    type="primary"
                    icon="el-icon-fa-check fas"
                    :disabled="activeTab !== 'library' || !hasSelection"
                    :loading="selecting"
                    @click="confirmSelection"
                ) {{ confirmButtonLabel }}
</template>

<i18n>
{
    "de": {
        "Media library": "Mediathek",
        "Public": "Öffentlich",
        "Private": "Privat",
        "Upload files": "Datei hochladen",
        "Search images or tags": "Bilder oder Tags suchen",
        "Search files or tags": "Dateien oder Tags suchen",
        "Filters": "Filter",
        "Filter media": "Medien filtern",
        "Filter by type": "Nach Typ filtern",
        "Filter by month": "Nach Monat filtern",
        "Filter by tags": "Nach Tags filtern",
        "Images": "Bilder",
        "Audio": "Audio",
        "Video": "Video",
        "Documents": "Dokumente",
        "Archives": "Archive",
        "Other": "Sonstige",
        "Reset filters": "Filter zurücksetzen",
        "No images found": "Es wurden keine Bilder gefunden",
        "No files found": "Es wurden keine Dateien gefunden",
        "Versions": "Versionen",
        "Version": "Version",
        "AI labelling": "KI-Kennzeichnung",
        "Fully AI-generated": "Vollständig KI-generiert",
        "Photo edited with AI": "Foto mit KI bearbeitet",
        "Marked as AI content": "Als KI-Inhalt gekennzeichnet",
        "Active": "Aktiv",
        "Original": "Original",
        "Crop": "Zuschnitt",
        "AI edit": "KI-Bearbeitung",
        "Variant": "Variante",
        "Use this version": "Diese Version verwenden",
        "Attachment details": "Dateidetails",
        "Multiple selection active": "Mehrfachauswahl aktiv",
        "Multiple file selection active": "Mehrfachauswahl für Dateien aktiv",
        "Select multiple images hint": "Mehrere Bilder per Klick oder Checkbox auswählen.",
        "Select multiple files hint": "Mehrere Dateien per Klick oder Checkbox auswählen.",
        "Selected images count": "{count} Bilder ausgewählt",
        "Selected files count": "{count} Dateien ausgewählt",
        "Clear selection": "Auswahl aufheben",
        "Name": "Name",
        "Type": "Typ",
        "Size": "Größe",
        "Date": "Datum",
        "Dimensions": "Abmessungen",
        "Tags": "Tags",
        "Loading": "Lade",
        "Select an image to see details": "Wähle ein Bild aus, um Details zu sehen",
        "Upload a new image or switch to the media library to choose an existing file.": "Lade ein neues Bild hoch oder wechsle in die Mediathek, um eine vorhandene Datei auszuwählen.",
        "Upload a new file or switch to the media library to choose an existing one.": "Lade eine neue Datei hoch oder wechsle in die Mediathek, um eine vorhandene Datei auszuwählen.",
        "Cancel": "Abbrechen",
        "Set crop": "Ausschnitt festlegen",
        "Use image": "Bild verwenden",
        "Use medium": "Medium verwenden",
        "Use selected media": "Medien verwenden",
        "Selected media count": "{count} Medien ausgewählt",
        "Use selected images": "Bilder verwenden",
        "Use file": "Datei verwenden",
        "Use selected files": "Dateien verwenden",
        "Selection limit reached": "Auswahllimit erreicht",
        "You can select up to {count} images.": "Du kannst maximal {count} Bilder auswählen.",
        "You can select up to {count} files.": "Du kannst maximal {count} Dateien auswählen.",
        "Could not load image": "Bild konnte nicht geladen werden",
        "Could not load file": "Datei konnte nicht geladen werden"
    },
    "en": {
        "Selected images count": "{count} images selected",
        "Selected files count": "{count} files selected",
        "Selected media count": "{count} media selected",
        "You can select up to {count} images.": "You can select up to {count} images.",
        "You can select up to {count} files.": "You can select up to {count} files."
    }
}
</i18n>

<script>
import _ from 'lodash'
import mime from 'mime'
import uniqid from 'uniqid'

import { useSetupStore } from '@/stores'
import { isFilePublic, fileAccessUrl, storageFileUrl } from '@/utils/fileAccessUrl'
import FileApi from '@/store/api/cms/file'
import Upload from '@/components/Partials/Upload'

export default {
    name: 'ImageLibraryDialog',
    components: {
        Upload,
    },
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        multiple: {
            type: Boolean,
            default: false,
        },
        selectionLimit: {
            type: Number,
            default: null,
        },
        acceptedMimeTypes: {
            type: Array,
            default: () => [],
        },
        selectionType: {
            type: String,
            default: 'image',
        },
        showCropButton: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            activeTab: 'library',
            filtersVisible: false,
            loading: false,
            loadingMore: false,
            selecting: false,
            variantPopoverFileId: null,
            selectedDimensionsLoading: false,
            selectedImageLoading: false,
            selectedImageLoadKey: '',
            selectedImagePreviewUrl: '',
            files: [],
            gridThumbLoaded: {},
            fileDimensions: {},
            fileDimensionsLoading: {},
            tagOptions: [],
            availableLibraryMimeTypes: [],
            selectedFiles: [],
            selectedFile: null,
            selectedDimensions: null,
            searchDebounce: null,
            browserScrollThrottle: null,
            pageInfo: {
                total: 0,
                perPage: 18,
                currentPage: 1,
                lastPage: 0,
            },
            filters: {
                query: '',
                types: [],
                createdAtMonth: null,
                tags: [],
            },
        }
    },
    computed: {
        dialogVisible: {
            get() {
                return this.visible
            },
            set(value) {
                this.$emit('update:visible', value)
            }
        },
        setupStore() {
            return useSetupStore()
        },
        activeCms() {
            return this.setupStore.activeCms
        },
        resolvedMimeTypes() {
            if (Array.isArray(this.acceptedMimeTypes) && this.acceptedMimeTypes.length) {
                return this.acceptedMimeTypes
            }
            if (this.isMediaSelection) {
                return [...this.imageMimeTypes, ...this.videoMimeTypes]
            }

            return this.isImageSelection ? this.imageMimeTypes : []
        },
        videoMimeTypes() {
            return [
                'video/mp4',
                'video/mpeg',
                'video/webm',
                'video/quicktime',
            ]
        },
        // Product and article galleries hold images AND videos, so their picker offers
        // both and emits whichever shape the chosen file calls for.
        isMediaSelection() {
            return this.selectionType === 'media'
        },
        // `selectionType` decides the SHAPE this dialog emits. It must not decide which
        // features exist: a version stack is available whenever the thing being picked can
        // be an image, and a product gallery ('media') picks images just as much as a
        // content image field ('image') does. Gating the stack on 'image' alone is what
        // made the badge render, the popover open, and every click do nothing in the
        // product picker.
        supportsVersions() {
            return this.isImageSelection || this.isMediaSelection
        },
        imageMimeTypes() {
            return [
                'image/jpeg',
                'image/png',
                'image/gif',
                'image/webp',
                'image/svg+xml',
            ]
        },
        hasMoreFiles() {
            return this.pageInfo.currentPage < this.pageInfo.lastPage
        },
        isImageSelection() {
            return this.selectionType === 'image'
        },
        searchPlaceholder() {
            return this.isImageSelection
                ? this.$t('Search images or tags')
                : this.$t('Search files or tags')
        },
        uploadDescription() {
            return this.isImageSelection
                ? this.$t('Upload a new image or switch to the media library to choose an existing file.')
                : this.$t('Upload a new file or switch to the media library to choose an existing one.')
        },
        uploadAccept() {
            return this.resolvedMimeTypes.join(',')
        },
        uploadTipFiletypes() {
            const extensions = _.chain(this.resolvedMimeTypes)
                .map(type => mime.getExtension(type))
                .filter(Boolean)
                .uniq()
                .value()

            return extensions.length ? extensions.join('/') : ''
        },
        availableMimeTypes() {
            return _.chain([
                ...this.resolvedMimeTypes,
                ...this.availableLibraryMimeTypes,
                ...this.selectedFiles.map(file => _.get(file, 'mime')),
                _.get(this.selectedFile, 'mime'),
            ])
                .filter(Boolean)
                .uniq()
                .sortBy(value => String(value).toLocaleLowerCase('de-DE'))
                .value()
        },
        useMimeTypeGroups() {
            return !this.resolvedMimeTypes.length
                || !this.resolvedMimeTypes.every(type => String(type || '').startsWith('image/'))
        },
        mediaTypeOptions() {
            const availableMimeTypes = this.availableMimeTypes

            if (!this.useMimeTypeGroups) {
                return _.chain(availableMimeTypes)
                    .map(mimeType => ({
                        value: mimeType,
                        label: this.getMimeTypeLabel(mimeType),
                        icon: 'el-icon-picture-outline',
                        mimes: [mimeType],
                    }))
                    .uniqBy(option => option.value)
                    .sortBy(option => String(option.label).toLocaleLowerCase('de-DE'))
                    .value()
            }

            return this.getMimeTypeGroups()
                .map(group => ({
                    ...group,
                    mimes: availableMimeTypes.filter(mimeType => group.match(mimeType)),
                }))
                .filter(group => group.mimes.length > 0)
        },
        tagFilterOptions() {
            const mergedTags = [
                ...this.tagOptions,
                ...this.files.flatMap(file => _.get(file, 'fileTags', [])),
                ...this.selectedFiles.flatMap(file => _.get(file, 'fileTags', [])),
                ...(_.get(this.selectedFile, 'fileTags', [])),
            ]

            return _.chain(mergedTags)
                .filter(tag => tag && tag.key && tag.name)
                .uniqBy(tag => String(tag.key))
                .sortBy(tag => String(tag.name).toLocaleLowerCase('de-DE'))
                .value()
        },
        selectedFileTags() {
            return this.getFileTags(this.selectedFile)
        },
        hasActiveFilters() {
            return !!(Array.isArray(this.filters.types) && this.filters.types.length)
                || !!(Array.isArray(this.filters.tags) && this.filters.tags.length)
                || !!this.filters.createdAtMonth
        },
        hasSelection() {
            return this.selectedFiles.length > 0
        },
        hasReachedSelectionLimit() {
            return this.selectionLimit !== null && this.selectedFiles.length >= this.selectionLimit
        },
        emptyStateLabel() {
            return this.isImageSelection
                ? this.$t('No images found')
                : this.$t('No files found')
        },
        multipleSelectionHint() {
            return this.isImageSelection
                ? this.$t('Select multiple images hint')
                : this.$t('Select multiple files hint')
        },
        selectedCountLabel() {
            if (this.isMediaSelection) {
                return this.$t('Selected media count', { count: this.selectedFiles.length })
            }

            return this.isImageSelection
                ? this.$t('Selected images count', { count: this.selectedFiles.length })
                : this.$t('Selected files count', { count: this.selectedFiles.length })
        },
        confirmButtonLabel() {
            // Media mode offers pictures AND clips, so neither "image" nor the generic
            // "file" wording fits what the editor is actually choosing.
            if (this.isMediaSelection) {
                if (!this.multiple) return this.$t('Use medium')
                if (!this.selectedFiles.length) return this.$t('Use selected media')

                return `${this.$t('Use selected media')} (${this.selectedFiles.length})`
            }

            if (this.isImageSelection) {
                if (!this.multiple) return this.$t('Use image')
                if (!this.selectedFiles.length) return this.$t('Use selected images')

                return `${this.$t('Use selected images')} (${this.selectedFiles.length})`
            }

            if (!this.multiple) return this.$t('Use file')
            if (!this.selectedFiles.length) return this.$t('Use selected files')

            return `${this.$t('Use selected files')} (${this.selectedFiles.length})`
        },
    },
    mounted() {
        this.initializeSearchDebounce()
        this.initializeBrowserScrollThrottle()
    },
    beforeDestroy() {
        this.cancelAsyncHandlers()
    },
    methods: {
        initializeSearchDebounce() {
            if (this.searchDebounce && typeof this.searchDebounce.cancel === 'function') return

            this.searchDebounce = _.debounce(async () => {
                await this.fetchFiles({ preserveSelection: true, page: 1 })
                this.scrollBrowserToTop()
            }, this.setupStore.debounce || 300)
        },
        initializeBrowserScrollThrottle() {
            if (this.browserScrollThrottle && typeof this.browserScrollThrottle.cancel === 'function') return

            this.browserScrollThrottle = _.throttle(event => {
                this.handleBrowserScrollInternal(event)
            }, 150)
        },
        cancelAsyncHandlers() {
            if (this.searchDebounce && typeof this.searchDebounce.cancel === 'function') {
                this.searchDebounce.cancel()
            }

            if (this.browserScrollThrottle && typeof this.browserScrollThrottle.cancel === 'function') {
                this.browserScrollThrottle.cancel()
            }
        },
        async handleOpen() {
            this.cancelAsyncHandlers()
            this.initializeSearchDebounce()
            this.initializeBrowserScrollThrottle()
            this.activeTab = 'library'
            this.files = []
            this.gridThumbLoaded = {}
            this.availableLibraryMimeTypes = []
            this.fileDimensions = {}
            this.fileDimensionsLoading = {}
            this.tagOptions = []
            this.pageInfo = {
                ...this.pageInfo,
                total: 0,
                currentPage: 1,
                lastPage: 0,
            }
            this.clearSelection()
            await this.fetchFiles()
        },
        handleClosed() {
            this.cancelAsyncHandlers()
            this.activeTab = 'library'
            this.clearSelection()
            this.gridThumbLoaded = {}
            this.fileDimensions = {}
            this.fileDimensionsLoading = {}
            this.selectedImageLoading = false
            this.selectedImageLoadKey = ''
            this.selectedImagePreviewUrl = ''
        },
        toggleFilters() {
            this.filtersVisible = !this.filtersVisible
        },
        handleSearchInput() {
            this.initializeSearchDebounce()
            this.searchDebounce()
        },
        async fetchFiles({ preserveSelection = false, append = false, page = 1 } = {}) {
            if (!_.get(this.activeCms, 'id')) return

            const isAppend = append && page > 1
            const previousGridThumbLoaded = { ...this.gridThumbLoaded }
            const requestPageInfo = {
                ...this.pageInfo,
                currentPage: page,
            }
            const selectedIds = this.selectedFiles.map(file => String(file.id))
            const activeSelectedId = _.get(this.selectedFile, 'id')

            if (isAppend) {
                this.loadingMore = true
            } else {
                this.loading = true
            }

            try {
                const filters = [
                    {
                        key: 'cms',
                        match: this.activeCms.id,
                    },
                    {
                        key: 'generated',
                        match: false,
                    },
                ]

                const selectedMimeTypes = this.getSelectedMimeTypes()
                if (selectedMimeTypes.length) {
                    filters.push({
                        key: 'mime',
                        in: selectedMimeTypes,
                    })
                }

                if (this.filters.query) {
                    filters.push({
                        key: 'name',
                        like: this.filters.query,
                    })
                }

                const createdAtFilter = this.buildCreatedAtFilter()
                if (createdAtFilter) {
                    filters.push(createdAtFilter)
                }

                if (Array.isArray(this.filters.tags) && this.filters.tags.length) {
                    filters.push({
                        key: 'tags',
                        in: this.filters.tags,
                    })
                }

                const data = await FileApi.fetchFilesPaginated({
                    scope: 'base',
                    type: 'file',
                    pageInfo: requestPageInfo,
                    variables: {
                        order: 'created_at',
                        direction: 'desc',
                    },
                    link: {
                        route: '/file/',
                    },
                    filters,
                // The full query carries imageVariantCount + imageVariants (and the tags
                // this dialog already renders) so the variant-stack badge/popover works.
                }, { full: true })

                const nextFiles = this.filterFilesBySelectedTypes(
                    (data.items || []).filter(file => this.matchesAcceptedMimeType(file))
                )
                const configuredMimeTypes = this.getConfiguredMimeTypes(data)

                this.files = isAppend
                    ? _.uniqBy([...this.files, ...nextFiles], file => String(file.id))
                    : nextFiles
                if (!isAppend) {
                    this.gridThumbLoaded = _.chain(nextFiles)
                        .map(file => this.getGridThumbLoadKey(file))
                        .filter(Boolean)
                        .reduce((accumulator, loadKey) => {
                            if (previousGridThumbLoaded[loadKey]) {
                                accumulator[loadKey] = true
                            }

                            return accumulator
                        }, {})
                        .value()
                }
                this.pageInfo = {
                    ...this.pageInfo,
                    ...data.pageInfo,
                }

                if (!this.filters.types.length) {
                    const knownMimeTypes = _.uniq([
                        ...configuredMimeTypes,
                        ...this.collectMimeTypes(isAppend ? nextFiles : this.files),
                    ])
                    this.availableLibraryMimeTypes = isAppend
                        ? _.uniq([ ...this.availableLibraryMimeTypes, ...knownMimeTypes ])
                        : knownMimeTypes
                } else if (configuredMimeTypes.length) {
                    this.availableLibraryMimeTypes = _.uniq([
                        ...this.availableLibraryMimeTypes,
                        ...configuredMimeTypes,
                    ])
                }

                if (preserveSelection && selectedIds.length) {
                    const nextSelectedFiles = selectedIds
                        .map(id => this.files.find(file => String(file.id) === String(id)))
                        .filter(Boolean)

                    if (nextSelectedFiles.length) {
                        this.selectedFiles = nextSelectedFiles
                        const activeMatch = nextSelectedFiles.find(file => String(file.id) === String(activeSelectedId))
                            || nextSelectedFiles[nextSelectedFiles.length - 1]
                        this.focusSelectedFile(activeMatch)
                    } else if (!isAppend) {
                        this.clearSelection()
                    }
                }

                if (!preserveSelection && !isAppend) {
                    this.clearSelection()
                }
            } finally {
                this.loading = false
                this.loadingMore = false
            }

            await this.$nextTick()
            this.ensureBrowserCanLoadMore()
        },
        async fetchTagOptions() {
            if (!_.get(this.activeCms, 'id')) {
                this.tagOptions = []
                return
            }

            try {
                const tags = await FileApi.fetchTagList(this.activeCms.id)
                this.tagOptions = _.chain(tags || [])
                    .filter(tag => tag && tag.key && tag.name)
                    .uniqBy(tag => String(tag.key))
                    .sortBy(tag => String(tag.name).toLocaleLowerCase('de-DE'))
                    .value()
            } catch (error) {
                this.tagOptions = []
            }
        },
        async handleSearchClear() {
            if (this.searchDebounce && typeof this.searchDebounce.cancel === 'function') {
                this.searchDebounce.cancel()
            }
            await this.fetchFiles({ preserveSelection: true, page: 1 })
            this.scrollBrowserToTop()
        },
        async handleFilterChange() {
            if (this.searchDebounce && typeof this.searchDebounce.cancel === 'function') {
                this.searchDebounce.cancel()
            }
            if (!Array.isArray(this.filters.types)) this.filters.types = []
            this.filters.types = this.normalizeMimeFilterValues(this.filters.types)
            await this.fetchFiles({ preserveSelection: true, page: 1 })
            this.scrollBrowserToTop()
        },
        async handleUploadChange(uploadedFile = null) {
            if (this.searchDebounce && typeof this.searchDebounce.cancel === 'function') {
                this.searchDebounce.cancel()
            }
            const uploadedFileId = _.get(uploadedFile, 'id') || _.get(uploadedFile, 'original_id')
            this.activeTab = 'library'
            this.files = []
            this.fileDimensions = {}
            this.fileDimensionsLoading = {}
            this.pageInfo = {
                ...this.pageInfo,
                total: 0,
                currentPage: 1,
                lastPage: 0,
            }
            this.clearSelection()
            await this.fetchFiles({ page: 1 })
            if (uploadedFileId) {
                let selectedFile = this.files.find(file => String(file.id) === String(uploadedFileId))
                if (!selectedFile && uploadedFile && this.matchesAcceptedMimeType(uploadedFile)) {
                    selectedFile = uploadedFile
                    this.files = _.uniqBy([uploadedFile, ...this.files], file => String(file.id))
                }
                if (selectedFile) this.selectFile(selectedFile)
            }
            this.scrollBrowserToTop()
        },
        async clearFilters() {
            if (this.searchDebounce && typeof this.searchDebounce.cancel === 'function') {
                this.searchDebounce.cancel()
            }
            this.filters.types = []
            this.filters.createdAtMonth = null
            this.filters.tags = []
            await this.fetchFiles({ preserveSelection: true, page: 1 })
            this.scrollBrowserToTop()
        },
        handleBrowserScroll(event) {
            this.initializeBrowserScrollThrottle()
            this.browserScrollThrottle(event)
        },
        handleBrowserScrollInternal(event) {
            const browser = event?.target || this.$refs.browser
            if (!browser || this.loading || this.loadingMore || !this.hasMoreFiles) return

            const remainingScroll = browser.scrollHeight - browser.scrollTop - browser.clientHeight
            if (remainingScroll <= 220) {
                this.loadMoreFiles()
            }
        },
        async loadMoreFiles() {
            if (this.loading || this.loadingMore || !this.hasMoreFiles) return

            await this.fetchFiles({
                append: true,
                preserveSelection: true,
                page: this.pageInfo.currentPage + 1,
            })
        },
        ensureBrowserCanLoadMore() {
            const browser = this.$refs.browser
            if (!browser || this.loading || this.loadingMore || !this.hasMoreFiles) return

            if (browser.scrollHeight <= browser.clientHeight + 40) {
                this.loadMoreFiles()
            }
        },
        matchesAcceptedMimeType(file) {
            const fileMime = _.get(file, 'mime')
            if (!fileMime) return false
            if (!this.resolvedMimeTypes.length) return true

            return this.resolvedMimeTypes.includes(fileMime)
        },
        canPreviewImage(file) {
            if (!this.getFileAccessUrl(file)) return false

            const mimeType = String(_.get(file, 'mime', '')).toLowerCase()
            if (_.startsWith(mimeType, 'image/')) return true

            const extension = String(this.getFileExtension(file)).toLowerCase()
            return extension === 'svg' || extension === 'svgz'
        },
        canPreviewVideo(file) {
            return !!this.getFileAccessUrl(file) && _.startsWith(_.get(file, 'mime', ''), 'video/')
        },
        canPreviewAudio(file) {
            return !!this.getFileAccessUrl(file) && _.startsWith(_.get(file, 'mime', ''), 'audio/')
        },
        hasMediaPreview(file) {
            return this.canPreviewImage(file)
                || this.canPreviewVideo(file)
                || this.canPreviewAudio(file)
        },
        getMimeTypeLabel(mimeType) {
            if (!mimeType) return ''

            switch (mimeType) {
                case 'image/jpeg':
                    return 'JPG / JPEG'
                case 'image/svg+xml':
                case 'image/svg':
                    return 'SVG'
                default:
                    return String(mime.getExtension(mimeType) || mimeType.split('/').pop() || mimeType).toUpperCase()
            }
        },
        getMimeTypeAliases() {
            return {
                jpg: 'image/jpeg',
                jpeg: 'image/jpeg',
                png: 'image/png',
                gif: 'image/gif',
                webp: 'image/webp',
                svg: 'image/svg+xml',
                pdf: 'application/pdf',
                mp4: 'video/mp4',
                webm: 'video/webm',
                mov: 'video/quicktime',
                qt: 'video/quicktime',
                mp3: 'audio/mpeg',
                wav: 'audio/wav',
                csv: 'text/csv',
                xls: 'application/vnd.ms-excel',
                xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                doc: 'application/msword',
                docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                zip: 'application/zip',
            }
        },
        getMimeTypeGroups() {
            return [
                {
                    value: 'images',
                    label: 'Images',
                    icon: 'el-icon-picture-outline',
                    match: mimeType => String(mimeType || '').startsWith('image/'),
                },
                {
                    value: 'audio',
                    label: 'Audio',
                    icon: 'el-icon-headset',
                    match: mimeType => String(mimeType || '').startsWith('audio/'),
                },
                {
                    value: 'video',
                    label: 'Video',
                    icon: 'el-icon-video-camera',
                    match: mimeType => String(mimeType || '').startsWith('video/'),
                },
                {
                    value: 'documents',
                    label: 'Documents',
                    icon: 'el-icon-document',
                    match: mimeType => this.isDocumentMimeType(mimeType),
                },
                {
                    value: 'archives',
                    label: 'Archives',
                    icon: 'el-icon-folder',
                    match: mimeType => this.isArchiveMimeType(mimeType),
                },
                {
                    value: 'other',
                    label: 'Other',
                    icon: 'el-icon-box',
                    match: mimeType => this.isOtherMimeType(mimeType),
                },
            ]
        },
        resolveMimeGroupValue(value) {
            if (!value) return null

            const normalizedGroupValue = String(value || '').toLowerCase()
            if (this.getMimeTypeGroups().some(option => option.value === normalizedGroupValue)) {
                return normalizedGroupValue
            }

            const mimeType = this.resolveMimeValue(value)
            if (!mimeType) return null

            const group = this.getMimeTypeGroups().find(option => option.match(mimeType))
            return group ? group.value : mimeType
        },
        resolveMimeValue(value) {
            if (!value) return null

            const aliases = this.getMimeTypeAliases()
            const normalizedValue = this.normalizeMimeType(value)

            if (normalizedValue.includes('/')) return normalizedValue

            return aliases[normalizedValue] || normalizedValue
        },
        normalizeMimeType(value) {
            const normalizedValue = String(value || '').toLowerCase()
            if (normalizedValue === 'image/svg') return 'image/svg+xml'
            return normalizedValue
        },
        normalizeMimeFilterValues(values = []) {
            return _.chain(values)
                .map(value => this.useMimeTypeGroups
                    ? this.resolveMimeGroupValue(value)
                    : this.resolveMimeValue(value)
                )
                .filter(Boolean)
                .uniq()
                .value()
        },
        collectMimeTypes(files = []) {
            return _.chain(files)
                .map(file => this.normalizeMimeType(_.get(file, 'mime')))
                .filter(Boolean)
                .uniq()
                .sortBy(value => String(value).toLocaleLowerCase('de-DE'))
                .value()
        },
        getConfiguredMimeTypes(data = {}) {
            return _.chain(_.get(data, 'config.allColumns', []))
                .find(column => _.get(column, 'key') === 'mime')
                .get('filter.options', [])
                .map(option => {
                    if (_.isString(option)) return option

                    return _.get(option, 'key')
                        || _.get(option, 'value')
                        || _.get(option, 'match')
                        || _.get(option, 'label')
                })
                .map(value => this.resolveMimeValue(value))
                .filter(value => value && String(value).includes('/'))
                .uniq()
                .sortBy(value => String(value).toLocaleLowerCase('de-DE'))
                .value()
        },
        isDocumentMimeType(mimeType) {
            const value = String(mimeType || '').toLowerCase()
            if (!value) return false
            if (value.startsWith('image/') || value.startsWith('audio/') || value.startsWith('video/')) return false

            return value === 'application/pdf'
                || value.startsWith('text/')
                || value.includes('word')
                || value.includes('officedocument.wordprocessingml')
                || value.includes('spreadsheetml')
                || value.includes('ms-excel')
                || value.includes('presentationml')
                || value.includes('ms-powerpoint')
                || value.includes('json')
                || value.includes('xml')
                || value.includes('rtf')
        },
        isArchiveMimeType(mimeType) {
            const value = String(mimeType || '').toLowerCase()
            if (!value) return false

            return value.includes('zip')
                || value.includes('rar')
                || value.includes('7z')
                || value.includes('tar')
                || value.includes('gzip')
                || value.includes('bzip')
                || value.includes('xz')
                || value.includes('compressed')
        },
        isOtherMimeType(mimeType) {
            const value = String(mimeType || '').toLowerCase()
            if (!value) return false

            return !String(value).startsWith('image/')
                && !String(value).startsWith('audio/')
                && !String(value).startsWith('video/')
                && !this.isDocumentMimeType(value)
                && !this.isArchiveMimeType(value)
        },
        getFileExtension(file) {
            const fileName = _.get(file, 'name', '')
            const extensionFromName = fileName.includes('.') ? fileName.split('.').pop() : ''
            const extension = extensionFromName || mime.getExtension(_.get(file, 'mime', '')) || 'file'

            return String(extension).toUpperCase()
        },
        getFileIconClass(file) {
            const extension = String(this.getFileExtension(file)).toLowerCase()

            switch (extension) {
                case 'pdf':
                    return 'el-icon-fa-file-pdf'
                case 'doc':
                case 'docx':
                case 'docm':
                case 'docb':
                case 'dotx':
                case 'dotm':
                    return 'el-icon-fa-file-word'
                case 'xls':
                case 'xlsx':
                case 'csv':
                    return 'el-icon-fa-file-excel'
                case 'ppt':
                case 'pptx':
                case 'pptm':
                    return 'el-icon-fa-file-powerpoint'
                case 'zip':
                case 'rar':
                case 'gz':
                case 'tar':
                case '7z':
                    return 'el-icon-fa-file-archive'
                case 'mp3':
                case 'wav':
                case 'ogg':
                    return 'el-icon-fa-file-audio'
                case 'mp4':
                case 'webm':
                case 'mov':
                case 'mpeg':
                    return 'el-icon-fa-file-video'
                case 'jpg':
                case 'jpeg':
                case 'png':
                case 'gif':
                case 'webp':
                case 'svg':
                    return 'el-icon-fa-file-image'
                default:
                    return 'el-icon-fa-file-alt'
            }
        },
        getFileTags(file) {
            return _.chain(_.get(file, 'fileTags', _.get(file, 'tags', [])) || [])
                .filter(tag => tag && (tag.name || tag.key))
                .map(tag => ({
                    ...tag,
                    name: tag.name || tag.key,
                }))
                .uniqBy(tag => String(tag.id || tag.key || tag.name))
                .value()
        },
        isSelected(file) {
            return this.selectedFiles.some(selectedFile => String(selectedFile.id) === String(file.id))
        },
        isFocused(file) {
            return String(_.get(this.selectedFile, 'id')) === String(file.id)
        },
        clearSelection() {
            this.selectedFiles = []
            this.focusSelectedFile(null)
        },
        focusSelectedFile(file) {
            this.selectedFile = file || null

            if (!file) {
                this.selectedDimensions = null
                this.selectedDimensionsLoading = false
                this.selectedImageLoading = false
                this.selectedImageLoadKey = ''
                this.selectedImagePreviewUrl = ''
                return
            }

            this.selectedDimensions = null
            this.selectedDimensionsLoading = false

            this.prepareSelectedImagePreview(file)
            this.loadSelectedDimensions(file)
        },
        getGridThumbLoadKey(file) {
            if (!file) return ''
            return `${_.get(file, 'id', '')}:${_.get(file, 'url', '')}`
        },
        isGridThumbLoaded(file) {
            const loadKey = this.getGridThumbLoadKey(file)
            if (!loadKey) return true
            return !!this.gridThumbLoaded[loadKey]
        },
        handleGridThumbLoad(file) {
            const loadKey = this.getGridThumbLoadKey(file)
            if (!loadKey || this.gridThumbLoaded[loadKey]) return
            this.$set(this.gridThumbLoaded, loadKey, true)
        },
        getSelectedImageLoadKey(file) {
            if (!file) return ''
            return `${_.get(file, 'id', '')}:${_.get(file, 'url', '')}`
        },
        prepareSelectedImagePreview(file) {
            if (!this.canPreviewImage(file)) {
                this.selectedImageLoading = false
                this.selectedImageLoadKey = ''
                this.selectedImagePreviewUrl = ''
                return
            }

            const nextLoadKey = this.getSelectedImageLoadKey(file)
            if (!nextLoadKey) {
                this.selectedImageLoading = false
                this.selectedImageLoadKey = ''
                this.selectedImagePreviewUrl = ''
                return
            }

            const previewUrl = this.getDetailPreviewUrl(file)
            if (!previewUrl) {
                this.selectedImageLoading = false
                this.selectedImageLoadKey = nextLoadKey
                this.selectedImagePreviewUrl = ''
                return
            }

            if (this.selectedImageLoadKey === nextLoadKey && this.selectedImagePreviewUrl === previewUrl && !this.selectedImageLoading) {
                return
            }

            this.selectedImageLoadKey = nextLoadKey
            this.selectedImageLoading = true
            this.selectedImagePreviewUrl = ''
            this.preloadSelectedImage(previewUrl, nextLoadKey)
        },
        preloadSelectedImage(url, loadKey) {
            if (typeof window === 'undefined' || typeof window.Image === 'undefined') {
                if (loadKey === this.selectedImageLoadKey) {
                    this.selectedImagePreviewUrl = url
                    this.selectedImageLoading = false
                }
                return
            }

            const image = new window.Image()
            image.onload = () => {
                if (loadKey !== this.selectedImageLoadKey) return
                this.selectedImagePreviewUrl = url
                this.selectedImageLoading = false
            }
            image.onerror = () => {
                if (loadKey !== this.selectedImageLoadKey) return
                this.selectedImagePreviewUrl = ''
                this.selectedImageLoading = false
            }
            image.src = url

            if (image.complete) {
                if (image.naturalWidth > 0 || image.width > 0) {
                    image.onload()
                } else {
                    image.onerror()
                }
            }
        },
        handleSelectedImageLoad(loadKey) {
            if (loadKey !== this.selectedImageLoadKey) return
            this.selectedImageLoading = false
        },
        handleSelectedImageError(loadKey) {
            if (loadKey !== this.selectedImageLoadKey) return
            this.selectedImageLoading = false
        },
        stopEvent() {},
        selectFile(file) {
            if (!this.multiple) {
                this.selectedFiles = [file]
                this.focusSelectedFile(file)
                return
            }

            if (this.isSelected(file)) {
                this.focusSelectedFile(file)
                return
            }

            if (this.selectionLimit !== null && this.selectedFiles.length >= this.selectionLimit) {
                this.$notify.warning({
                    title: this.$t('Selection limit reached'),
                    message: this.isImageSelection
                        ? this.$t('You can select up to {count} images.', { count: this.selectionLimit })
                        : this.$t('You can select up to {count} files.', { count: this.selectionLimit }),
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 5000,
                })
                return
            }

            this.selectedFiles = [...this.selectedFiles, file]
            this.focusSelectedFile(file)
        },
        handleFileClick(file) {
            this.selectFile(file)
        },
        toggleFileSelection(file) {
            if (!this.multiple) {
                this.selectFile(file)
                return
            }

            const fileId = String(file.id)
            const isAlreadySelected = this.selectedFiles.some(selectedFile => String(selectedFile.id) === fileId)

            if (isAlreadySelected) {
                this.selectedFiles = this.selectedFiles.filter(selectedFile => String(selectedFile.id) !== fileId)
                if (String(_.get(this.selectedFile, 'id')) === fileId) {
                    this.focusSelectedFile(this.selectedFiles[this.selectedFiles.length - 1] || null)
                }
                return
            }

            if (this.selectionLimit !== null && this.selectedFiles.length >= this.selectionLimit) {
                this.$notify.warning({
                    title: this.$t('Selection limit reached'),
                    message: this.isImageSelection
                        ? this.$t('You can select up to {count} images.', { count: this.selectionLimit })
                        : this.$t('You can select up to {count} files.', { count: this.selectionLimit }),
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 5000,
                })
                return
            }

            this.selectedFiles = [...this.selectedFiles, file]
            this.focusSelectedFile(file)
        },
        async loadSelectedDimensions(file) {
            if (!this.canPreviewImage(file)) {
                this.selectedDimensions = null
                this.selectedDimensionsLoading = false
                return
            }

            const fileId = String(file.id)
            const cachedDimensions = this.fileDimensions[fileId]

            if (cachedDimensions) {
                this.selectedDimensions = cachedDimensions
                this.selectedDimensionsLoading = false
                return
            }

            this.selectedDimensions = null
            this.selectedDimensionsLoading = true

            try {
                const dimensions = await this.loadImageDimensions(this.getFileAccessUrl(file))
                if (String(_.get(this.selectedFile, 'id')) === fileId) {
                    this.selectedDimensions = dimensions
                }
                this.$set(this.fileDimensions, fileId, dimensions)
            } catch (error) {
                if (String(_.get(this.selectedFile, 'id')) === fileId) {
                    this.selectedDimensions = null
                }
            } finally {
                if (String(_.get(this.selectedFile, 'id')) === fileId) {
                    this.selectedDimensionsLoading = false
                }
            }
        },
        loadImageDimensions(src) {
            return new Promise((resolve, reject) => {
                const image = new window.Image()
                image.onload = () => {
                    resolve({
                        width: image.naturalWidth || image.width || 100,
                        height: image.naturalHeight || image.height || 100,
                    })
                }
                image.onerror = reject
                image.src = src
            })
        },
        getPreviewUrl(file, width = 260, height = 180) {
            const baseUrl = this.getPreviewBaseUrl(file)
            if (!baseUrl) return ''
            const mimeType = String(_.get(file, 'mime', '')).toLowerCase()

            // SVGs should be loaded directly; crop query params can break vector previews.
            if (mimeType === 'image/svg+xml' || mimeType === 'image/svg') {
                return baseUrl
            }

            try {
                const previewUrl = new URL(baseUrl, window.location.origin)
                previewUrl.searchParams.delete('download')
                previewUrl.searchParams.set('fit', 'crop')
                previewUrl.searchParams.set('w', String(width))
                previewUrl.searchParams.set('h', String(height))
                previewUrl.searchParams.set('crop', 'focalpoint')
                previewUrl.searchParams.set('x', '.5')
                previewUrl.searchParams.set('y', '.5')
                previewUrl.searchParams.set('z', '1')
                return previewUrl.toString()
            } catch (_) {
                const separator = baseUrl.includes('?') ? '&' : '?'
                return `${baseUrl}${separator}fit=crop&w=${width}&h=${height}&crop=focalpoint&x=.5&y=.5&z=1`
            }
        },
        getPreviewSrcSet(file, width = 260, height = 180, scales = [1, 2]) {
            const mimeType = String(_.get(file, 'mime', '')).toLowerCase()
            if (mimeType === 'image/svg+xml' || mimeType === 'image/svg') return ''

            const normalizedScales = _.chain(scales)
                .map(scale => Number(scale))
                .filter(scale => Number.isFinite(scale) && scale > 0)
                .uniq()
                .sortBy()
                .value()

            if (!normalizedScales.length) return ''

            const entries = normalizedScales
                .map(scale => {
                    const scaledWidth = Math.max(1, Math.round(width * scale))
                    const scaledHeight = Math.max(1, Math.round(height * scale))
                    const url = this.getPreviewUrl(file, scaledWidth, scaledHeight)
                    if (!url) return ''
                    return `${url} ${scaledWidth}w`
                })
                .filter(Boolean)

            return entries.join(', ')
        },
        getDetailPreviewUrl(file) {
            const baseUrl = this.getPreviewBaseUrl(file)
            if (!baseUrl) return ''

            const mimeType = String(_.get(file, 'mime', '')).toLowerCase()
            if (mimeType === 'image/svg+xml' || mimeType === 'image/svg') {
                return baseUrl
            }

            try {
                const previewUrl = new URL(baseUrl, window.location.origin)
                previewUrl.searchParams.delete('download')
                previewUrl.searchParams.set('w', '900')
                previewUrl.searchParams.set('h', '900')
                previewUrl.searchParams.delete('fit')
                previewUrl.searchParams.delete('crop')
                previewUrl.searchParams.delete('x')
                previewUrl.searchParams.delete('y')
                previewUrl.searchParams.delete('z')
                return previewUrl.toString()
            } catch (_) {
                return baseUrl
            }
        },
        getPreviewBaseUrl(file) {
            const fileUrl = this.getFileAccessUrl(file)
            if (!fileUrl) return ''

            const usesStorageRoute = /\/storage\/(public|private)\//.test(fileUrl)
            if (usesStorageRoute) return fileUrl

            const storageUrl = this.getStorageFileUrl(file)
            return storageUrl || fileUrl
        },
        isFilePublic(file) {
            return isFilePublic(file)
        },
        getFileAccessUrl(file) {
            return fileAccessUrl(file)
        },
        getStorageFileUrl(file) {
            return storageFileUrl(file)
        },
        formatFileSize(value) {
            const size = Number(value)
            if (!size) return '0 B'

            const units = ['B', 'KB', 'MB', 'GB', 'TB']
            const index = Math.min(Math.floor(Math.log(size) / Math.log(1024)), units.length - 1)
            const amount = size / (1024 ** index)
            const precision = amount >= 10 || index === 0 ? 0 : 1

            return `${amount.toFixed(precision)} ${units[index]}`
        },
        formatFileDate(value) {
            if (value === undefined || value === null || value === '') return ''

            let normalizedValue = value
            if (typeof normalizedValue === 'string' && /^\d+$/.test(normalizedValue)) {
                normalizedValue = Number(normalizedValue)
            }

            let date
            if (typeof normalizedValue === 'number') {
                const timestamp = normalizedValue > 9999999999 ? normalizedValue : normalizedValue * 1000
                date = new Date(timestamp)
            } else {
                date = new Date(normalizedValue)
            }

            if (Number.isNaN(date.getTime())) return ''

            return new Intl.DateTimeFormat('de-DE', {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
            }).format(date)
        },
        formatFileMeta(file) {
            return [this.getFileExtension(file), this.formatFileSize(_.get(file, 'size'))]
                .filter(Boolean)
                .join(' / ')
        },
        filterFilesBySelectedTypes(files) {
            const selectedMimeTypes = this.getSelectedMimeTypes()
            if (!selectedMimeTypes.length) return files

            return files.filter(file => selectedMimeTypes.includes(this.normalizeMimeType(_.get(file, 'mime'))))
        },
        getSelectedMimeTypes() {
            const selectedTypes = _.chain(this.filters.types)
                .map(value => String(value || '').toLowerCase())
                .filter(Boolean)
                .uniq()
                .value()

            if (!Array.isArray(selectedTypes) || !selectedTypes.length) {
                return this.resolvedMimeTypes
            }

            const selectedOptions = this.mediaTypeOptions.filter(option => selectedTypes.includes(option.value))
            const mimeTypes = selectedOptions.flatMap(option => option.mimes)

            if (mimeTypes.length) return _.uniq(mimeTypes)

            return this.normalizeMimeFilterValues(selectedTypes)
        },
        buildCreatedAtFilter() {
            if (!this.filters.createdAtMonth) return null

            const [yearString, monthString] = String(this.filters.createdAtMonth).split('-')
            const year = parseInt(yearString, 10)
            const month = parseInt(monthString, 10)

            if (!year || !month) return null

            const fromDate = new Date(year, month - 1, 1, 0, 0, 0, 0)
            const toDate = new Date(year, month, 0, 23, 59, 59, 999)

            return {
                key: 'created_at',
                from: Math.floor(fromDate.getTime() / 1000),
                to: Math.floor(toDate.getTime() / 1000),
            }
        },
        scrollBrowserToTop() {
            const browser = this.$refs.browser
            if (!browser) return

            if (typeof browser.scrollTo === 'function') {
                browser.scrollTo({
                    top: 0,
                    behavior: 'auto',
                })
                return
            }

            browser.scrollTop = 0
        },
        // Variant-stack popover helpers (shared shape with FilesList).
        variantKindIcon(kind) {
            if (kind === 'original') return 'el-icon-fa-image'
            if (kind === 'crop') return 'el-icon-fa-crop-alt'
            if (kind === 'ai-edit') return 'el-icon-fa-wand-magic-sparkles'
            return 'el-icon-fa-layer-group'
        },
        variantKindLabel(kind) {
            if (kind === 'original') return this.$t('Original')
            if (kind === 'crop') return this.$t('Crop')
            if (kind === 'ai-edit') return this.$t('AI edit')
            return this.$t('Variant')
        },
        // Pick a specific version (crop / AI edit) from the stack. The pick REFERENCES
        // the version — it selects the file the source image already renders and carries
        // the whole stack along (pointer + versions), so the editor shows Original + the
        // picked version and the backend clones that stack onto the new image
        // (Image::adoptLibraryFile). Nothing is copied: an eager server-side flatten put a
        // duplicate root-level file in the library on every pick, including picks the user
        // then cancelled.
        // Picking "Original" is just picking the source file itself.
        async pickVariant(file, variant) {
            this.variantPopoverFileId = null

            if (!variant || variant.kind === 'original' || !_.get(variant, 'file.id')) {
                this.selectFile(file)
                if (!this.multiple) await this.confirmSelection()
                return
            }

            if (!this.supportsVersions) return

            const image = this.buildSelectedImage(file, {
                width: variant.width || null,
                height: variant.height || null,
            })
            const token = _.get(variant, 'file.type')
            image.processing_query_string = token
            image.processedFile = variant.file
            image.activeVariant = variant
            image.variants = (file.imageVariants || []).map(entry => ({
                ...entry,
                active: _.get(entry, 'file.type') === token,
            }))

            // Media hosts expect the same {kind, payload} envelope confirmSelection emits
            // for them; a bare image would arrive as an unrecognised entry.
            const selected = this.isMediaSelection ? { kind: 'image', payload: image } : image

            this.$emit('select', this.multiple ? [selected] : selected)
            this.dialogVisible = false
        },
        // What the file is declared as, phrased for someone CHOOSING it. Editing happens
        // in the file manager, where a file has a proper form with a Save.
        aiDisclosureLabel(file) {
            const sourceType = _.get(file, 'ai_source_type')

            if (sourceType === 'trainedAlgorithmicMedia') return this.$t('Fully AI-generated')
            if (sourceType === 'compositeWithTrainedAlgorithmicMedia') return this.$t('Photo edited with AI')

            return this.$t('Marked as AI content')
        },
        buildSelectedImage(file, dimensions) {
            const size = _.get(file, 'size')

            return {
                id: `new:file-library:${file.id}:${uniqid()}`,
                alt: '',
                description: file.description || null,
                width: dimensions.width,
                height: dimensions.height,
                name: file.name,
                preferred_name: file.preferred_name,
                mime: file.mime,
                size: size !== undefined && size !== null ? String(size) : '',
                url: file.url,
                // A short-lived public link for private files (the raw url 403s for the
                // browser preview, Meta, and customers) — consumers pick url vs signed_url
                // by visibility. Mirrors buildSelectedFile.
                signed_url: file.signed_url,
                visibility: file.visibility,
                source: {
                    id: parseInt(file.id, 10),
                    type: file.type || 'original',
                    name: file.name,
                    preferred_name: file.preferred_name,
                    description: file.description || null,
                    mime: file.mime,
                    size: size !== undefined && size !== null ? String(size) : '',
                    url: file.url,
                    // Art. 50 record. It belongs to the FILE, so picking an AI image out
                    // of the library has to bring its disclosure, its kind and the
                    // rationale with it - otherwise the editor gets an unlabelled image
                    // and no sign that it needs labelling.
                    ai_source_type: file.ai_source_type || null,
                    ai_disclosure: file.ai_disclosure === true,
                    ai_label_display: file.ai_label_display || null,
                    ai_assessment: _.cloneDeep(file.ai_assessment) || null,
                },
            }
        },
        // The shape a freshly UPLOADED video has, minus `upload: true` - that flag is what
        // tells the API to move an uploads-table row into place. A library file is shared,
        // so the API adopts it where it lies. Dimensions are left to the backend, which
        // reuses another video's measurements of the same file or probes it once.
        buildSelectedVideo(file) {
            const fileId = parseInt(file.id, 10)

            return {
                id: `new:file-library:${fileId}:${uniqid()}`,
                source: {
                    id: fileId,
                    type: file.type || 'original',
                    name: file.name,
                    preferred_name: file.preferred_name,
                    mime: file.mime,
                    size: _.get(file, 'size') !== undefined ? String(_.get(file, 'size')) : '',
                    url: file.url,
                    // Art. 50 record travels with the file, exactly as it does for images.
                    ai_source_type: file.ai_source_type || null,
                    ai_disclosure: file.ai_disclosure === true,
                    ai_label_display: file.ai_label_display || null,
                },
            }
        },
        buildSelectedFile(file) {
            const parsedId = parseInt(file.id, 10)
            const size = _.get(file, 'size')

            return {
                id: Number.isNaN(parsedId) ? file.id : parsedId,
                name: file.name,
                preferred_name: file.preferred_name,
                mime: file.mime,
                size: size !== undefined && size !== null ? String(size) : '',
                url: file.url,
                // A short-lived public link for private files (Meta/customers can't fetch the
                // private url) — consumers pick url vs signed_url by visibility.
                signed_url: file.signed_url,
                path: file.path,
                visibility: file.visibility,
                type: file.type || 'original',
                fileTags: _.cloneDeep(this.getFileTags(file)),
                created_at: file.created_at,
                updated_at: file.updated_at,
            }
        },
        async confirmSelection() {
            const files = this.multiple ? this.selectedFiles : (this.selectedFile ? [this.selectedFile] : [])
            if (!files.length) return

            this.selecting = true

            try {
                if (this.isMediaSelection) {
                    const media = await Promise.all(files.map(async file => {
                        if (!this.videoMimeTypes.includes(file.mime)) {
                            const cached = this.fileDimensions[String(file.id)]
                            const dimensions = cached || await this.loadImageDimensions(this.getFileAccessUrl(file))
                            if (!cached) this.$set(this.fileDimensions, String(file.id), dimensions)

                            return { kind: 'image', payload: this.buildSelectedImage(file, dimensions) }
                        }

                        return { kind: 'video', payload: this.buildSelectedVideo(file) }
                    }))

                    this.$emit('select', this.multiple ? media : media[0])
                } else if (this.isImageSelection) {
                    const images = await Promise.all(files.map(async file => {
                        const cachedDimensions = this.fileDimensions[String(file.id)]
                        const dimensions = cachedDimensions || await this.loadImageDimensions(this.getFileAccessUrl(file))

                        if (!cachedDimensions) {
                            this.$set(this.fileDimensions, String(file.id), dimensions)
                        }

                        return this.buildSelectedImage(file, dimensions)
                    }))

                    this.$emit('select', this.multiple ? images : images[0])
                } else {
                    const selectedFiles = files.map(file => this.buildSelectedFile(file))
                    this.$emit('select', this.multiple ? selectedFiles : selectedFiles[0])
                }
                this.dialogVisible = false
            } catch (error) {
                this.$notify.error({
                    title: this.$t('An error occurred!'),
                    message: this.isImageSelection
                        ? this.$t('Could not load image')
                        : this.$t('Could not load file'),
                    iconClass: 'fas el-icon-fa-times',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000,
                })
            } finally {
                this.selecting = false
            }
        },
        async confirmSelectionWithCrop() {
            const file = this.selectedFile
            if (!file) return
            this.selecting = true
            try {
                const cachedDimensions = this.fileDimensions[String(file.id)]
                const dimensions = cachedDimensions || await this.loadImageDimensions(this.getFileAccessUrl(file))
                if (!cachedDimensions) this.$set(this.fileDimensions, String(file.id), dimensions)
                this.$emit('select-with-crop', this.buildSelectedImage(file, dimensions))
                this.dialogVisible = false
            } catch (error) {
                this.$notify.error({
                    title: this.$t('An error occurred!'),
                    message: this.$t('Could not load image'),
                    iconClass: 'fas el-icon-fa-times',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000,
                })
            } finally {
                this.selecting = false
            }
        },
    },
}
</script>

<style lang="sass" scoped>
// ── Dialog shell ──────────────────────────────────────────────────────────────
:deep(.image-library-dialog)
    height: calc(100vh - 32px)
    max-height: calc(100vh - 32px)
    max-width: calc(100vw - 32px)
    margin: 16px auto !important
    display: flex
    flex-direction: column
    border-radius: 10px
    overflow: hidden
    .el-dialog__header
        padding: 16px 20px
        border-bottom: 1px solid #ebeef5
        background: #fff
    .el-dialog__title
        font-size: 15px
        font-weight: 600
        color: #1a1a2e
    .el-dialog__headerbtn
        top: 14px
    .el-dialog__body
        flex: 1
        min-height: 0
        overflow: hidden
        padding: 0
    .el-dialog__footer
        border-top: 1px solid #ebeef5
        background: #fff
        padding: 12px 20px

// ── Tab header ────────────────────────────────────────────────────────────────
.image-library
    height: 100%
    min-height: 0
    display: flex
    flex-direction: column
    background: #f8f9fb
    &:deep(.el-tabs__header)
        margin: 0
        padding: 0 20px
        background: #fff
        border-bottom: 1px solid #ebeef5
    &:deep(.el-tabs__nav-wrap::after)
        display: none
    &:deep(.el-tabs__item)
        font-size: 13px
        font-weight: 500
        color: #7a8499
        height: 44px
        line-height: 44px
        padding: 0 16px
        &.is-active
            color: #409eff
            font-weight: 600
    &:deep(.el-tabs__active-bar)
        height: 2px
        border-radius: 2px
    &:deep(.el-tabs__content)
        flex: 1
        min-height: 0
        overflow: hidden
    &:deep(.el-tab-pane)
        height: 100%
        min-height: 0

.image-library__tabs
    flex: 1
    min-height: 0
    display: flex
    flex-direction: column

// ── Upload tab ────────────────────────────────────────────────────────────────
.image-library__upload-tab
    height: 100%
    min-height: 0
    display: flex
    align-items: flex-start
    justify-content: center
    overflow: auto
    padding: 24px 20px

.image-library__upload-panel
    width: min(100%, 720px)
    border-radius: 8px
    border: 1px solid #e8eaed
    padding: 20px
    background: #fff
    box-shadow: 0 1px 3px rgba(0,0,0,0.04)
    h4
        margin: 0 0 8px
        font-size: 14px
        font-weight: 600
        color: #2c3044
    p
        margin: 0 0 18px
        color: #a0a8b8
        font-size: 13px

// ── Library tab ───────────────────────────────────────────────────────────────
.image-library__library-tab
    height: 100%
    min-height: 0
    display: flex
    flex-direction: column
    gap: 0

.image-library__toolbar
    display: flex
    gap: 10px
    align-items: center
    flex-wrap: wrap
    padding: 12px 20px
    background: #fff
    border-bottom: 1px solid #ebeef5
    flex-shrink: 0

.image-library__search
    flex: 1
    min-width: 240px
    :deep(.el-input__inner)
        border-radius: 6px
        border-color: #e0e3e8
        background: #fff
        &:focus
            border-color: #409eff

.image-library__toolbar-actions
    display: flex
    align-items: center
    gap: 10px
    margin-left: auto

// ── Selection bar ─────────────────────────────────────────────────────────────
.image-library__selection-bar
    display: flex
    align-items: center
    justify-content: space-between
    gap: 12px
    flex-wrap: wrap
    padding: 8px 20px
    background: #f2f4f7
    border-bottom: 1px solid #e8eaed
    flex-shrink: 0

.image-library__selection-hint
    margin: 0
    color: #7a8499
    font-size: 12px

.image-library__selection-chip
    display: inline-flex
    align-items: center
    gap: 10px
    background: transparent
    color: #7a8499
    font-size: .85rem
    line-height: 1
    margin-left: auto

.image-library__selection-status
    font-weight: 600
    color: #2c3044

.image-library__selection-action
    appearance: none
    background: #fff
    border: 1px solid #e0e3e8
    margin: 0
    padding: 5px 10px
    border-radius: 999px
    color: #5a6275
    font-size: .8rem
    font-weight: 500
    line-height: 1
    cursor: pointer
    transition: color .15s, border-color .15s, background .15s
    &:hover
        color: #409eff
        border-color: rgba(#409eff, .35)
        background: rgba(#409eff, .04)

// ── Filters ───────────────────────────────────────────────────────────────────
.image-library__filters
    display: flex
    flex-wrap: wrap
    gap: 10px
    align-items: center
    padding: 10px 20px
    background: #f8f9fb
    border-bottom: 1px solid #ebeef5
    flex-shrink: 0

.image-library__filters-title
    font-size: 12px
    font-weight: 600
    color: #2c3044

.image-library__filters-controls
    display: flex
    flex: 1
    flex-wrap: wrap
    gap: 10px
    align-items: center

.image-library__filter-select
    width: 220px

.image-library__filter-month
    width: 300px

.image-library__select-option
    display: inline-flex
    align-items: center
    gap: 8px
    i
        width: 14px
        text-align: center
        color: #7a8499

// ── Body & grid ───────────────────────────────────────────────────────────────
.image-library__body
    display: grid
    grid-template-columns: minmax(0, 1fr)
    min-height: 0
    flex: 1
    align-items: stretch
    overflow: hidden

.image-library__body--with-sidebar
    grid-template-columns: minmax(0, 1fr) 280px

.image-library__browser
    height: 100%
    min-height: 0
    display: flex
    flex-direction: column
    gap: 0
    overflow: auto
    padding: 16px 20px
    background: #fff

.image-library__grid
    display: grid
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr))
    gap: 12px

// ── Item cards ────────────────────────────────────────────────────────────────
.image-library__item
    display: flex
    flex-direction: column
    height: 100%
    cursor: pointer
    border: 1px solid #e8eaed
    border-radius: 8px
    overflow: hidden
    box-shadow: 0 1px 3px rgba(0,0,0,0.04)
    transition: transform .15s, border-color .15s, box-shadow .15s
    &:deep(.el-card__body)
        display: flex
        flex-direction: column
        flex: 1
        height: 100%
        padding: 0
    &:hover
        transform: translateY(-1px)
        box-shadow: 0 3px 10px rgba(64,158,255,0.12)
        border-color: #b8d4f8
    &.is-focused
        border-color: #409eff
        box-shadow: 0 0 0 2px rgba(#409eff, .12)
    &.is-active
        border-color: #409eff
        box-shadow: 0 0 0 3px rgba(#409eff, .18)

.image-library__item-content
    display: flex
    flex-direction: column
    height: 100%

.image-library__thumb
    position: relative
    aspect-ratio: 13 / 9
    background: #f0f3f8
    display: flex
    align-items: center
    justify-content: center
    overflow: hidden
    img
        width: 100%
        height: 100%
        object-fit: cover
        display: block
    &--loading .image-library__thumb-image
        opacity: 0

.image-library__thumb-image
    position: relative
    z-index: 1
    transition: opacity .2s ease

.image-library__thumb-skeleton
    position: absolute
    inset: 0
    z-index: 1
    background: linear-gradient(90deg, #eef2f7 25%, #f6f8fb 37%, #eef2f7 63%)
    background-size: 400% 100%
    animation: image-library-skeleton-shimmer 1.2s ease-in-out infinite

.image-library__file-thumb
    width: 100%
    height: 100%
    display: flex
    flex-direction: column
    align-items: center
    justify-content: center
    gap: 10px
    color: #7a8499
    i
        font-size: 2.2rem
    span
        font-size: .82rem
        font-weight: 700
        letter-spacing: .06em
        color: #a0a8b8

.image-library__variant-badge
    position: absolute
    bottom: 6px
    left: 6px
    z-index: 2
    display: inline-flex
    align-items: center
    gap: 4px
    padding: 2px 7px
    border-radius: 11px
    font-size: 11px
    font-weight: 600
    color: #fff
    background: rgba(110, 38, 255, .92)
    box-shadow: 0 1px 3px rgba(0, 0, 0, .3)
    cursor: pointer
    transition: background .15s ease
    &:hover
        background: rgba(110, 38, 255, 1)
    i
        font-size: 10px

.image-library__checkbox
    position: absolute
    top: 8px
    right: 8px
    z-index: 2
    display: flex
    align-items: center
    justify-content: center
    width: 26px
    height: 26px
    border-radius: 999px
    background: rgba(255,255,255,.96)
    box-shadow: 0 2px 8px rgba(0,0,0,0.12)
    &:deep(.el-checkbox)
        display: flex
        align-items: center
        justify-content: center
    &:deep(.el-checkbox__label)
        display: none
    &:deep(.el-checkbox__input)
        line-height: 1

.image-library__meta
    flex: 1
    display: flex
    flex-direction: column
    padding: 9px 11px 11px

.image-library__name
    font-weight: 600
    font-size: 13px
    line-height: 1.35
    color: #2c3044
    word-break: break-word

.image-library__details
    margin-top: 3px
    color: #a0a8b8
    font-size: .84rem

.image-library__visibility.indicator
    position: absolute
    top: 8px
    left: 8px
    z-index: 2
    box-shadow: 0 0 0 2px #fff

.image-library__tags
    margin-top: auto
    padding-top: 6px
    display: flex
    flex-wrap: wrap
    gap: 3px
    align-items: center
    &:deep(.el-tag)
        margin: 0
    &:deep(.el-tag + .el-tag)
        margin-left: 0

.image-library__tags-more
    color: #a0a8b8
    font-size: .75rem
    line-height: 1

// ── Sidebar ───────────────────────────────────────────────────────────────────
.image-library__sidebar
    height: 100%
    display: flex
    flex-direction: column
    gap: 12px
    min-height: 0
    overflow: auto
    padding: 16px
    border-left: 1px solid #e8eaed
    background: #fff

.image-library__selection
    border: 1px solid #e8eaed
    border-radius: 8px
    padding: 14px
    background: #fff
    box-shadow: 0 1px 3px rgba(0,0,0,0.04)
    display: flex
    flex-direction: column
    gap: 10px
    h4
        margin: 0
        font-size: 13px
        font-weight: 600
        color: #2c3044
    &--empty
        min-height: 240px
        align-items: center
        justify-content: center
        text-align: center
        color: #a0a8b8
        i
            font-size: 2rem

.image-library__selected-preview
    border-radius: 6px
    overflow: hidden
    background: #f0f3f8
    &--file
        min-height: 160px

.image-library__selected-image-stage
    position: relative
    width: 100%
    height: 220px
    border-radius: 6px
    overflow: hidden
    background: #f0f3f8

.image-library__selected-image
    width: 100%
    height: 100%
    object-fit: contain
    display: block

.image-library__selected-image-skeleton
    position: absolute
    inset: 0
    z-index: 1
    background: linear-gradient(90deg, #eef2f7 25%, #f6f8fb 37%, #eef2f7 63%)
    background-size: 400% 100%
    animation: image-library-skeleton-shimmer 1.2s ease-in-out infinite

.image-library__selected-video
    width: 100%
    display: block
    background: #000
    border-radius: 6px

.image-library__selected-audio
    min-height: 160px
    display: flex
    flex-direction: column
    align-items: center
    justify-content: center
    gap: 14px
    padding: 18px
    color: #7a8499
    i
        font-size: 2.2rem
    audio
        width: 100%

.image-library__selected-file
    min-height: 160px
    display: flex
    flex-direction: column
    align-items: center
    justify-content: center
    gap: 10px
    color: #7a8499
    i
        font-size: 2.6rem
    span
        font-size: .9rem
        font-weight: 700
        letter-spacing: .08em
        color: #a0a8b8

.image-library__detail
    display: flex
    flex-direction: column
    gap: 3px
    font-size: .85rem
    strong
        font-size: 11px
        font-weight: 600
        color: #a0a8b8
        text-transform: uppercase
        letter-spacing: .04em
    span
        color: #2c3044
        word-break: break-word

.image-library__detail-tags
    display: flex
    flex-wrap: wrap
    gap: 6px

// ── Version stack (sidebar) ───────────────────────────────────────────────────
.image-library__detail--variants
    gap: 8px

.image-library__variant-list
    display: flex
    flex-direction: column
    gap: 6px

.image-library__variant
    display: flex
    align-items: center
    gap: 10px
    padding: 6px
    border: 1px solid #e7eaf0
    border-radius: 8px
    background: #fff
    cursor: pointer
    transition: border-color .15s ease, background .15s ease
    &:hover
        border-color: #6E26FF
        background: #faf7ff
    &.is-active
        border-color: #6E26FF
        background: #f3edff
    &:hover .image-library__variant-pick
        color: #6E26FF
        transform: translateX(2px)

.image-library__variant-thumb
    flex: 0 0 auto
    width: 44px
    height: 44px
    border-radius: 6px
    overflow: hidden
    background: #f0f3f8
    display: flex
    align-items: center
    justify-content: center
    color: #a0a8b8
    img
        width: 100%
        height: 100%
        object-fit: cover
        display: block

.image-library__variant-body
    flex: 1 1 auto
    min-width: 0

.image-library__variant-kind
    display: flex
    align-items: center
    gap: 6px
    font-size: 12px
    font-weight: 600
    color: #2c3044
    i
        color: #6E26FF
        font-size: 12px

.image-library__variant-active
    margin-left: auto
    font-size: 10px
    font-weight: 600
    text-transform: uppercase
    letter-spacing: .04em
    color: #6E26FF
    background: #ece3ff
    padding: 1px 6px
    border-radius: 8px

.image-library__variant-label
    font-size: 11px
    color: #6b7280
    margin-top: 2px
    overflow: hidden
    text-overflow: ellipsis
    white-space: nowrap

.image-library__variant-dims
    font-size: 11px
    color: #a0a8b8
    margin-top: 1px

.image-library__variant-pick
    flex: 0 0 auto
    color: #c0c8d4
    font-size: 12px
    transition: color .15s ease, transform .15s ease

// ── Empty & loading ───────────────────────────────────────────────────────────
.image-library__empty
    min-height: 280px
    display: flex
    flex-direction: column
    align-items: center
    justify-content: center
    gap: 10px
    color: #c0c8d4
    i
        font-size: 2.4rem
        opacity: 0.5
    p
        font-size: 13px
        color: #a0a8b8

.image-library__loading-more
    display: flex
    align-items: center
    justify-content: center
    gap: 8px
    padding: 8px 0 14px
    color: #a0a8b8
    font-size: 13px

// ── Footer ────────────────────────────────────────────────────────────────────
.modal-footer
    display: flex
    justify-content: space-between
    align-items: center

@keyframes image-library-skeleton-shimmer
    0%
        background-position: 100% 0
    100%
        background-position: 0 0

@media (max-width: 1100px)
    .image-library__body
        grid-template-columns: 1fr
        overflow: auto
    .image-library__browser
        overflow: visible
    .image-library__sidebar
        overflow: visible
        border-left: none
        border-top: 1px solid #e8eaed

@media (max-width: 640px)
    .image-library__toolbar-actions
        margin-left: 0
    .image-library__selection-chip
        width: 100%
        margin-left: 0
        justify-content: space-between
    .image-library__selection-bar
        align-items: stretch
    .image-library__filters-controls
        width: 100%
    .image-library__filter-select
        width: 100%
    .image-library__filter-month
        width: 100%
    .image-library__selected-image-stage
        height: 200px
</style>
